/**
 * Utility functions for working with JWT tokens
 */

/**
 * Decodes a JWT token and extracts the payload
 * @param token - The JWT token to decode
 * @returns The decoded payload or null if invalid
 */
export function decodeJWT(token: string): any | null {
  try {
    // JWT tokens have 3 parts separated by dots: header.payload.signature
    const parts = token.split('.')
    if (parts.length !== 3) {
      return null
    }

    // Decode the payload (second part)
    const payload = parts[1]

    // Add padding if needed for base64 decoding
    const paddedPayload = payload + '='.repeat((4 - (payload.length % 4)) % 4)

    // Decode base64
    const decodedPayload = atob(paddedPayload)

    // Parse JSON
    return JSON.parse(decodedPayload)
  } catch (error) {
    console.error('Error decoding JWT token:', error)
    return null
  }
}

/**
 * Extracts the user ID from a JWT token
 * @param token - The JWT token to extract user ID from
 * @returns The user ID or null if not found
 */
export function extractUserIdFromToken(token: string): string | null {
  const payload = decodeJWT(token)
  if (!payload) {
    return null
  }

  // Firebase custom tokens typically have 'uid' field
  // Firebase ID tokens typically have 'user_id' field
  return payload.uid || payload.user_id || payload.sub || null
}

/**
 * Checks if a token belongs to the current user
 * @param token - The JWT token to check
 * @param currentUserId - The current user's ID
 * @returns True if the token belongs to the current user, false otherwise
 */
export function isTokenForCurrentUser(token: string, currentUserId: string): boolean {
  const tokenUserId = extractUserIdFromToken(token)
  return tokenUserId === currentUserId
}
